<template>
  <div class="sidenav navbar navbar-vertical fixed-left navbar-expand-xs navbar-light bg-white"
       @mouseenter="$sidebar.onMouseEnter()"
       @mouseleave="$sidebar.onMouseLeave()"
       :data="backgroundColor">
    <div class="scrollbar-inner" ref="sidebarScrollArea">
      <div class="sidenav-header d-flex align-items-center">
        <a class="navbar-brand d-flex align-items-center" href="#">
          <!-- MadameFin mark (2026-10-03). It used to fall back to
               /favicon.png — the round nine-square grid of the Argon
               template, which told nobody whose backoffice this is. -->
          <img :src="logo" class="navbar-brand-img navbar-brand-img--mdf" alt="MadameFin">
          <!-- Wordmark, not a translated string: "BackOffice" is the name of
               the product next to the MadameFin mark, the same in th and en.
               It lives inside the same <a> as the logo, so both lead to the
               same place, and it is hidden on the collapsed rail where there
               is no room for it. -->
          <span class="navbar-brand-text--mdf font-weight-bold ml-2">BackOffice</span>
        </a>
        <!-- `sidenav-toggler-wrap` names the wrapper so the collapsed-rail
             rule below can cancel `.ml-auto` there and centre the toggler
             (spec 2026-10-07-bo-sidebar-toggler-collapsed-design.md T1). -->
        <div class="ml-auto sidenav-toggler-wrap">
          <!-- Sidenav toggler -->
          <div class="sidenav-toggler d-none d-xl-block"
               :class="{'active': !$sidebar.isMinimized }"
               :title="$sidebar.isMinimized ? $t('sidebar.expand') : $t('sidebar.collapse')"
               :aria-label="$sidebar.isMinimized ? $t('sidebar.expand') : $t('sidebar.collapse')"
               role="button"
               @click="minimizeSidebar">
            <div class="sidenav-toggler-inner">
              <i class="sidenav-toggler-line"></i>
              <i class="sidenav-toggler-line"></i>
              <i class="sidenav-toggler-line"></i>
            </div>
          </div>
        </div>
      </div>
      <slot></slot>
      <div class="navbar-inner">
        <ul class="navbar-nav">
          <slot name="links">
            <sidebar-item
              v-for="(link, index) in sidebarLinks"
              :key="link.name + index"
              :link="link"
            >
              <sidebar-item
                v-for="(subLink, index) in link.children"
                :key="subLink.name + index"
                :link="subLink"
              >
              </sidebar-item>
            </sidebar-item>
          </slot>
        </ul>
        <slot name="links-after"></slot>
      </div>
    </div>
  </div>
</template>
<script>
// Imported (not referenced as "/img/logo.png") so webpack fingerprints it and
// the sidebar cannot end up showing a stale or missing file after a deploy.
import brandLogo from '@/assets/img/brand/madamefin-logo.png';

export default {
  name: 'sidebar',
  props: {
    title: {
      type: String,
      default: 'Creative Tim',
      description: 'Sidebar title'
    },
    shortTitle: {
      type: String,
      default: 'CT',
      description: 'Sidebar short title'
    },
    logo: {
      type: String,
      default: brandLogo,
      description: 'Sidebar app logo'
    },
    backgroundColor: {
      type: String,
      default: 'vue',
      validator: value => {
        let acceptedValues = [
          '',
          'vue',
          'blue',
          'green',
          'orange',
          'red',
          'primary'
        ];
        return acceptedValues.indexOf(value) !== -1;
      },
      description:
        'Sidebar background color (vue|blue|green|orange|red|primary)'
    },
    sidebarLinks: {
      type: Array,
      default: () => [],
      description:
        "List of sidebar links as an array if you don't want to use components for these."
    },
    autoClose: {
      type: Boolean,
      default: true,
      description:
        'Whether sidebar should autoclose on mobile when clicking an item'
    }
  },
  data() {
    return {
      // Every <sidebar-item> under this sidebar registers here (SidebarItem
      // mounted/destroyed). Component instances are not made reactive by Vue;
      // the array itself is, so a leaf's isActive re-evaluates when rows
      // appear or disappear (role guards, pinned section).
      activeRegistry: { items: [] }
    };
  },
  provide() {
    return {
      autoClose: this.autoClose,
      sidebarActiveRegistry: this.activeRegistry
    };
  },
  methods: {
    minimizeSidebar() {
      if (this.$sidebar) {
        this.$sidebar.toggleMinimize();
      }
    }
  },
  mounted() {
    this.$sidebar.isMinimized = this.$sidebar.breakpoint < window.innerWidth
    this.minimizeSidebar()
  },
  beforeDestroy() {
    if (this.$sidebar.showSidebar) {
      this.$sidebar.showSidebar = false;
    }
  }
};
</script>
<style lang="scss">
/* The page on screen, the way Seller Center marks it (mdf-seller-fn
   src/assets/sass/custom/_seller-theme.scss "Sidebar" block): the active leaf
   is accent text, semi-bold, on a soft tint of the accent with a 3px accent
   bar on the left; a group header whose child is active gets the accent text
   only (no fill, no bar), so exactly one row reads as "here". Which row is
   active is decided in JS (activeMatch.js via SidebarItem.isActive), not by
   router-link's string-prefix class.

   Accent = BO's brand blue `$app_bg` (custom/_variables.scss, #0065c3, also
   `$info`). `$primary` is #0c1026 — near-black, indistinguishable from the
   row ink — so it cannot do the job seller's maroon does. Contrast, pinned by
   tests/unit/sidebarActiveHighlight.spec.js: #0065c3 on the 8% tint
   (#ebf3fa) is 5.13:1, on white 5.75:1 (AA normal text >= 4.5).
   Not scoped and not in argon.scss: the compiled theme is pinned
   byte-identical by other specs, and the rail state is a class on <body>. */
$bo-sidebar-accent: #0065c3;
$bo-sidebar-accent-soft: #ebf3fa; // mix($bo-sidebar-accent, #fff, 8%), rounded

.sidenav.navbar-vertical.navbar-light .navbar-nav .nav-link.active:not(.sidebar-menu-item) {
	color: $bo-sidebar-accent;
	background-color: $bo-sidebar-accent-soft;
	box-shadow: inset 3px 0 0 $bo-sidebar-accent;
	font-weight: 600;
}

.sidenav.navbar-vertical.navbar-light .navbar-nav .nav-link.sidebar-menu-item.active {
	color: $bo-sidebar-accent;
	background-color: transparent;
}

/* Collapsed 62px rail: submenus are hidden (theme: `.nav-item .collapse
   { display: none !important }`), so a group whose child is the page is the
   only row left to say "here". Accent text alone is not enough there — the
   label is hidden and several icons are already $info (#0065c3, e.g. คลังสินค้า)
   — so on the rail the group header takes the leaf treatment (tint + bar).
   It stays inside the rail: the theme's 0.5rem side margins apply to it as
   to a top-level leaf (measured 8..53px in a 62px rail). Hover-expanded and
   pinned states do not match and keep the text-only header. */
body.g-sidenav-hidden:not(.g-sidenav-show) .sidenav.navbar-vertical.navbar-light .navbar-nav .nav-link.sidebar-menu-item.active,
body.g-sidenav-hide .sidenav.navbar-vertical.navbar-light .navbar-nav .nav-link.sidebar-menu-item.active {
	background-color: $bo-sidebar-accent-soft;
	box-shadow: inset 3px 0 0 $bo-sidebar-accent;
}

/* Menu icons carry their own `.text-blue` / `.text-primary` / ... utilities,
   which are `!important`; the active row's icon follows the accent. */
.sidenav.navbar-vertical.navbar-light .navbar-nav .nav-link.active > i {
	color: $bo-sidebar-accent !important;
}

/* The mark is a circle on a black field, so it is given a round frame and a
   size of its own instead of the template's flat 2rem cap. Not scoped: the
   minimized state is signalled by a class on <body>
   (SidebarPlugin/index.js -> g-sidenav-hidden), which a scoped selector on
   this component cannot reach.

   `.navbar-vertical` has to be in the selector: core/navbars/_navbar-vertical.scss
   caps `.navbar-vertical .navbar-brand-img` at 2rem, and a bare
   `.navbar-brand-img--mdf` (0,1,0) loses to it (0,2,0) no matter the order —
   the mark would silently stay at the template's size. */
.navbar-vertical .navbar-brand-img--mdf {
	max-height: 2.5rem;
	width: auto;
	border-radius: 50%;
	transition: max-height 0.15s ease;
}

body.g-sidenav-hidden:not(.g-sidenav-show) .navbar-vertical .navbar-brand-img--mdf {
	/* Collapsed rail: the logo has to fit beside the toggler, and the wordmark
	   under the monogram is unreadable at this size anyway. */
	max-height: 1.75rem;
}

/* "BackOffice" next to the mark. $gray-800 on the white sidenav = 12.6:1;
   `.navbar-brand` is the anchor, so without an explicit colour the word would
   inherit the link blue and read as something clickable-to-somewhere-else. */
.navbar-vertical .navbar-brand-text--mdf {
	color: #32325d;
	font-size: 1rem;
	letter-spacing: 0.01em;
	line-height: 1.2;
	white-space: nowrap;
}

/* The collapsed rail is ~62px wide — the word does not fit and must not be
   what pushes it open. The template already hides the whole `.navbar-brand`
   in this state (core/navbars/_navbar-vertical.scss:343); this says it of the
   word directly, so the text does not reappear if that ever changes. */
body.g-sidenav-hidden:not(.g-sidenav-show) .navbar-vertical .navbar-brand-text--mdf,
body.g-sidenav-hide .navbar-vertical .navbar-brand-text--mdf {
	display: none;
}

/* The ☰ toggler on the collapsed rail (spec 2026-10-07-bo-sidebar-toggler-
   collapsed-design.md T1). The theme gives it `padding: 1.5rem` at >=xl
   (core/navbars/_navbar-vertical.scss, `.sidenav .sidenav-toggler`, 0,2,0):
   24 + 18 + 24 = 66px, wider than the 62px rail, and `.ml-auto` pushed it to
   the right edge where `.sidenav`'s overflow cut it off — the owner saw an
   empty header and no way back except hovering. Here it is centred and
   narrowed to 12 + 18 + 12 = 42px. These selectors are (0,4,1)/(0,5,1), so
   they beat the theme's rule on specificity, not on bundle order; the
   expanded states (g-sidenav-show on hover, g-sidenav-pinned) do not match
   and keep the theme's layout untouched (T2).

   Line colour (T4): `.sidenav-toggler-line` is theme-color("default")
   = #172b4d (rgb 23,43,77, measured in Chrome) on the bg-white sidenav,
   about 14.1:1 — visible as is, so no
   `.sidenav-toggler-dark` (which would make the lines white on white). */
body.g-sidenav-hidden:not(.g-sidenav-show) .navbar-vertical .sidenav-header {
	justify-content: center;
}

/* The spec (§2, T2 "โลโก้กลับมา") takes the theme's word that the brand is
   hidden on the rail (`.sidenav .navbar-brand { display: none }`). It is not:
   the <a> carries bootstrap's `.d-flex` = `display: flex !important`, so the
   logo kept a 48px box on the rail. Measured in Chrome at 1366: with the
   toggler centred beside it, the pair is 90px in a 61px header and the
   toggler ends at x=75.5 — still cut off. Hidden here for real, with
   !important because that is the only thing that beats `.d-flex`. The logo
   returns on hover (g-sidenav-show) and when pinned, where this rule does not
   match. (The 1.75rem collapsed max-height rule above is moot while this
   stands.) */
body.g-sidenav-hidden:not(.g-sidenav-show) .navbar-vertical .sidenav-header > .navbar-brand {
	display: none !important;
}

body.g-sidenav-hidden:not(.g-sidenav-show) .navbar-vertical .sidenav-header > .sidenav-toggler-wrap {
	/* bootstrap's .ml-auto is `margin-left: auto !important` */
	margin-left: 0 !important;
}

body.g-sidenav-hidden:not(.g-sidenav-show) .navbar-vertical .sidenav-toggler {
	padding: 1.5rem 0.75rem;
}

/* Hover while collapsed (T2b, ruling 2026-10-07 after the render check).
   Entering the rail adds g-sidenav-show and the theme's `.sidenav:hover`
   widens it to 250px; with the theme layout the ☰ would move from x 10–52 to
   x 183–249, so the pointer that just reached it lands on the logo link
   instead and the click never pins. Keep the ☰ exactly where it was while the
   sidebar is only hover-expanded: first in the header (order -1), 10px in,
   same 12px side padding. Both pinned paths add g-sidenav-pinned, so a pinned
   sidebar keeps the theme's layout (logo left, ☰ right).
   `g-sidenav-hide` is the 300ms collapse animation after the pointer leaves
   (SidebarPlugin/index.js onMouseLeave); without it the ☰ flickered to the
   right and back during that window. */
body.g-sidenav-show:not(.g-sidenav-pinned) .navbar-vertical .sidenav-header > .sidenav-toggler-wrap,
body.g-sidenav-hide .navbar-vertical .sidenav-header > .sidenav-toggler-wrap {
	order: -1;
	margin-left: 10px !important;
}

body.g-sidenav-show:not(.g-sidenav-pinned) .navbar-vertical .sidenav-toggler,
body.g-sidenav-hide .navbar-vertical .sidenav-toggler {
	padding: 1.5rem 0.75rem;
}
</style>
